import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  taxonomyListBlockingSchemasQueryOptions,
  useDeleteTaxonomyList,
  type TaxonomyListSummary,
} from '@/entities/taxonomy-list';
import { FormDialog } from '@/shared/ui/FormDialog';

interface DeleteListDialogProps {
  list: TaxonomyListSummary;
  onClose: () => void;
}

/**
 * Confirms deleting a shared vocabulary.
 *
 * Rendered by the PAGE rather than the row menu: on success the row
 * disappears, and a Radix dialog unmounted while still open leaves
 * `body { pointer-events: none }` behind, which freezes the entire page.
 *
 * The backend refuses to delete a list that a schema's head version or any
 * published version binds to. The dialog names those same schemas, so the
 * user sees why and what to change instead of a generic error.
 */
export function DeleteListDialog({ list, onClose }: DeleteListDialogProps) {
  const { t } = useTranslation('knowledge');
  const remove = useDeleteTaxonomyList();

  // Mounted only while confirming, so the index never fires one of these per row.
  const blockersQuery = useQuery(taxonomyListBlockingSchemasQueryOptions(list.id));
  const blockers = blockersQuery.data ?? [];

  return (
    <FormDialog
      open
      onOpenChange={(next) => !next && onClose()}
      title={t('taxonomy.deleteListDialog.deleteNamed', { name: list.name })}
      className="flex flex-col sm:!max-w-[28rem]"
    >
      <FormDialog.Body className="flex flex-col gap-3">
        {blockersQuery.isError ? (
          <p className="text-sm text-muted-foreground">
            {t('taxonomy.deleteListDialog.couldNotCheckUsage')}
          </p>
        ) : blockersQuery.isFetching ? (
          <p className="text-sm text-muted-foreground">
            {t('taxonomy.deleteListDialog.checkingUsage')}
          </p>
        ) : blockers.length > 0 ? (
          <>
            <p className="text-sm text-muted-foreground">
              {t('taxonomy.deleteListDialog.schemasStillBind', { count: blockers.length })}
            </p>
            <ul className="flex flex-col gap-0.5">
              {blockers.map((s) => (
                <li key={s.taxonomyId} className="truncate text-xs text-muted-foreground">
                  {s.taxonomyName}
                  <span className="ml-1 opacity-70">({s.fieldNames.join(', ')})</span>
                </li>
              ))}
            </ul>
          </>
        ) : (
          <p className="text-sm text-muted-foreground">
            {t('taxonomy.deleteListDialog.noSchemaUsesList', { count: list.rowCount })}
          </p>
        )}
      </FormDialog.Body>
      <FormDialog.Footer
        primary={{
          label: t('taxonomy.deleteListDialog.deleteList'),
          onClick: () => remove.mutate(list.id, { onSuccess: onClose }),
          disabled:
            blockers.length > 0 ||
            remove.isPending ||
            blockersQuery.isFetching ||
            blockersQuery.isError,
          loading: remove.isPending,
        }}
        secondary={{ label: t('taxonomy.deleteListDialog.cancel'), onClick: onClose }}
      />
    </FormDialog>
  );
}

DeleteListDialog.displayName = 'DeleteListDialog';
